import React, { useState, useEffect, useRef } from 'react'
import UserAvatar from 'components/shared/UserAvatar'
import TeacherCard from 'components/TeamCourseBooking/TeacherCard'
import Calendar from 'components/Availability/Calendar'
import AvailabilityTimeSlotList from 'components/Availability/TimeSlotList'
import TimezoneSelector from 'components/Booking/TimezoneSelector'
import RecurringBookingPreview from 'components/shared/RecurringBookingPreview'
import { saveTimezoneToServer } from 'utils'
import { trackEvent } from 'utils/analytics'

const STEPS = {
  COURSE: 'course',
  TEACHER: 'teacher',
  TIMESLOT: 'timeslot'
}

// 從 Date 物件提取指定時區的 HH:MM
function formatTimeInTz(date, timezone) {
  if (!timezone) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  }
  // 使用 Intl 取得精確的時/分
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: timezone
  }).formatToParts(date)
  const h = parts.find(p => p.type === 'hour')?.value || '00'
  const m = parts.find(p => p.type === 'minute')?.value || '00'
  return `${h}:${m}`
}

// 將 API 的 flat slot array 轉為 Calendar/TimeSlotList 需要的 date-grouped 格式
function transformSlots(flatSlots, timezone) {
  const grouped = {}
  flatSlots.forEach(slot => {
    const dt = new Date(slot.start_time)
    const endDt = new Date(slot.end_time)

    const dateKey = timezone
      ? dt.toLocaleDateString('en-CA', { timeZone: timezone })
      : `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`

    if (!grouped[dateKey]) grouped[dateKey] = []
    grouped[dateKey].push({
      start: formatTimeInTz(dt, timezone),
      end: formatTimeInTz(endDt, timezone),
      start_time: slot.start_time,
      end_time: slot.end_time
    })
  })
  return grouped
}

export default function BookingModal({ balances, courses, colors, onClose, onBooked, onPurchaseMore, preselectedCourseId, userTimezone, translations: t = {} }) {
  const [step, setStep] = useState(STEPS.COURSE)
  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const [selectedTeacher, setSelectedTeacher] = useState(null)
  const [teachers, setTeachers] = useState([])
  const [loadingTeachers, setLoadingTeachers] = useState(false)
  const [availabilityData, setAvailabilityData] = useState({})
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [booking, setBooking] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [balanceExhausted, setBalanceExhausted] = useState(false)
  const [selectedTimezone, setSelectedTimezone] = useState(null)
  const [isFirstBooking, setIsFirstBooking] = useState(false)
  const timeSlotsRef = useRef(null)

  const handleClose = () => {
    onClose({ firstBooking: isFirstBooking })
  }

  useEffect(() => {
    trackEvent('booking_modal_open')
  }, [])

  // 只顯示有餘額的課程
  const coursesWithBalance = balances.filter(b => b.remainingLessons > 0)

  // 初始化：單課程自動跳過選課、preselectedCourseId 自動選擇
  useEffect(() => {
    const targetCourseId = preselectedCourseId || (coursesWithBalance.length === 1 ? coursesWithBalance[0].courseId : null)
    if (targetCourseId) {
      setSelectedCourseId(targetCourseId)
      setStep(STEPS.TEACHER)
    }
  }, [])

  // 選課程後載入老師列表
  useEffect(() => {
    if (selectedCourseId && step === STEPS.TEACHER) {
      fetchTeachers(selectedCourseId)
    }
  }, [selectedCourseId, step])

  // 選老師後載入可用時段（等 timezone 初始化完成）
  useEffect(() => {
    if (selectedTeacher && step === STEPS.TIMESLOT && selectedTimezone) {
      fetchSlots(selectedCourseId, selectedTeacher.id, selectedTimezone)
    }
  }, [selectedTeacher, step, selectedTimezone])

  const fetchTeachers = async (courseId) => {
    try {
      setLoadingTeachers(true)
      setError(null)
      const response = await fetch(`/api/team_courses/${courseId}/teachers`)
      if (!response.ok) throw new Error('Failed to load teacher list')
      const data = await response.json()
      let teacherList = data.teachers || []

      const preferredId = sessionStorage.getItem('preferredTeacherUserId')
      if (preferredId) {
        sessionStorage.removeItem('preferredTeacherUserId')
        const id = Number(preferredId)
        teacherList = [
          ...teacherList.filter(t => t.id === id),
          ...teacherList.filter(t => t.id !== id)
        ]
      }

      setTeachers(teacherList)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoadingTeachers(false)
    }
  }

  const fetchSlots = async (courseId, teacherId, timezone = selectedTimezone, monthDate = null) => {
    try {
      setLoadingSlots(true)
      setError(null)
      setSelectedSlot(null)
      setSelectedDate(null)

      const params = new URLSearchParams()
      if (timezone) params.set('timezone', timezone)
      if (monthDate) {
        const startDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
        const endDate = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0)
        const pad = (n) => String(n).padStart(2, '0')
        params.set('start_date', `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}`)
        params.set('end_date', `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`)
      }
      const queryString = params.toString() ? `?${params.toString()}` : ''
      const response = await fetch(`/api/team_courses/${courseId}/teachers/${teacherId}/available_slots${queryString}`)
      if (!response.ok) throw new Error('Failed to load available slots')
      const data = await response.json()
      const grouped = transformSlots(data.slots || [], timezone)
      setAvailabilityData(grouped)

      // 自動選中第一個有可用時段的日期（初次載入時）
      if (!monthDate) {
        const firstDateKey = Object.keys(grouped).sort()[0]
        if (firstDateKey) {
          const [y, m, d] = firstDateKey.split('-').map(Number)
          const firstDate = new Date(y, m - 1, d)
          setSelectedDate(firstDate)
          setCurrentMonth(new Date(y, m - 1, 1))
        }
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoadingSlots(false)
    }
  }

  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth)
    if (selectedTeacher && selectedCourseId && selectedTimezone) {
      fetchSlots(selectedCourseId, selectedTeacher.id, selectedTimezone, newMonth)
    }
  }

  const handleCourseSelect = (courseId) => {
    setSelectedCourseId(courseId)
    setSelectedTeacher(null)
    setSelectedSlot(null)
    setAvailabilityData({})
    setSelectedDate(null)
    setBalanceExhausted(false)
    setStep(STEPS.TEACHER)
  }

  const handleTeacherSelect = (teacher) => {
    trackEvent('booking_teacher_selected', { teacher_name: teacher.name })
    setSelectedTeacher(teacher)
    setSelectedSlot(null)
    setAvailabilityData({})
    setSelectedDate(null)
    setBalanceExhausted(false)
    setStep(STEPS.TIMESLOT)
  }

  const handleBack = () => {
    setError(null)
    if (step === STEPS.TIMESLOT) {
      setSelectedTeacher(null)
      setSelectedSlot(null)
      setAvailabilityData({})
      setSelectedDate(null)
      setStep(STEPS.TEACHER)
    } else if (step === STEPS.TEACHER && coursesWithBalance.length > 1 && !preselectedCourseId) {
      setSelectedCourseId(null)
      setTeachers([])
      setStep(STEPS.COURSE)
    }
  }

  const handleTimezoneChange = (newTimezone) => {
    setSelectedTimezone(newTimezone)
    saveTimezoneToServer(newTimezone)
  }

  const handleDateSelect = (date) => {
    setSelectedDate(date)
    setSelectedSlot(null)
    // Scroll to time slots on mobile (single column layout)
    setTimeout(() => {
      if (timeSlotsRef.current) {
        timeSlotsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 150)
  }

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot)
    setSuccessMessage(null)
  }

  const handleBook = async (batchSlots) => {
    if (!selectedSlot || !selectedCourseId || !selectedTeacher) return

    try {
      setBooking(true)
      setError(null)

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const headers = { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken }

      let bookedCount = 1

      if (batchSlots) {
        // Batch booking
        const response = await fetch(`/api/team_courses/${selectedCourseId}/bookings/batch`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            teacher_id: selectedTeacher.id,
            slots: batchSlots
          })
        })
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || 'Batch booking failed')
        bookedCount = data.summary.success
        if (data.first_booking) setIsFirstBooking(true)
      } else {
        // Single booking
        const response = await fetch(`/api/team_courses/${selectedCourseId}/bookings`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            teacher_id: selectedTeacher.id,
            start_time: selectedSlot.start_time
          })
        })
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || data.errors?.join(', ') || 'Booking failed')
        if (data.first_booking) setIsFirstBooking(true)
      }

      trackEvent('booking_completed', {
        course_id: selectedCourseId,
        teacher_name: selectedTeacher.name,
        booked_count: bookedCount,
        is_batch: !!batchSlots
      })
      onBooked(selectedCourseId, bookedCount)
      setSuccessMessage({
        teacherName: selectedTeacher.name,
        time: selectedSlot.start,
        date: selectedDate,
        count: bookedCount
      })
      setSelectedSlot(null)
      const currentBalance = balances.find(b => b.courseId === selectedCourseId)
      if (currentBalance && currentBalance.remainingLessons <= bookedCount) {
        setAvailabilityData({})
        setBalanceExhausted(true)
      } else {
        fetchSlots(selectedCourseId, selectedTeacher.id)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setBooking(false)
    }
  }

  // 取得選中日期的時段
  const selectedDateSlots = (() => {
    if (!selectedDate) return []
    const year = selectedDate.getFullYear()
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
    const day = String(selectedDate.getDate()).padStart(2, '0')
    const dateKey = `${year}-${month}-${day}`
    return availabilityData[dateKey] || []
  })()

  const hasAnySlots = Object.keys(availabilityData).length > 0

  const canGoBack = step === STEPS.TIMESLOT || (step === STEPS.TEACHER && coursesWithBalance.length > 1 && !preselectedCourseId)

  const selectedBalance = coursesWithBalance.find(b => b.courseId === selectedCourseId)
  const selectedCourseData = courses?.find(c => c.id === selectedCourseId)

  const stepTitle = {
    [STEPS.COURSE]: t.selectCourse || 'Select Course',
    [STEPS.TEACHER]: t.selectTeacher || 'Select Teacher',
    [STEPS.TIMESLOT]: t.selectTimeSlot || 'Select Time Slot'
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 md:p-4">
      <div className={`bg-white w-full h-full md:h-auto md:max-h-[90vh] ${step === STEPS.TIMESLOT ? 'md:max-w-3xl' : 'md:max-w-lg'} md:mx-4 md:rounded-2xl md:shadow-xl overflow-hidden flex flex-col transition-all`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {canGoBack && (
              <button
                onClick={handleBack}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-lg font-bold text-gray-900">{stepTitle[step]}</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress indicator */}
        <div className="px-4 pt-3">
          <div className="flex items-center gap-1">
            {(coursesWithBalance.length > 1 && !preselectedCourseId ? [STEPS.COURSE, STEPS.TEACHER, STEPS.TIMESLOT] : [STEPS.TEACHER, STEPS.TIMESLOT]).map((s, idx, arr) => (
              <React.Fragment key={s}>
                <div
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    arr.indexOf(step) >= idx ? '' : 'bg-gray-200'
                  }`}
                  style={arr.indexOf(step) >= idx ? { backgroundColor: colors.primary } : {}}
                />
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {successMessage && (
            <div
              className="mb-4 p-4 rounded-xl text-white"
              style={{ background: colors.primaryGradient }}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <div className="font-bold text-base">{t.bookingConfirmed || 'Booking confirmed!'}</div>
                  <div className="text-sm text-white/80">
                    {successMessage.date && successMessage.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} {successMessage.time} · {successMessage.teacherName}
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">{t.continueBooking || 'You can continue selecting a time slot for your next lesson.'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Step: Course Selection */}
          {step === STEPS.COURSE && (
            <div className="space-y-3">
              {coursesWithBalance.map((balance) => (
                <button
                  key={balance.courseId}
                  onClick={() => handleCourseSelect(balance.courseId)}
                  className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all text-left cursor-pointer"
                >
                  <div>
                    <span className="font-medium text-gray-900">{balance.courseName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold" style={{ color: colors.primary }}>
                      {(t.lessonsLeft || '%{count} lesson(s) left').replace('%{count}', balance.remainingLessons)}
                    </span>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step: Teacher Selection */}
          {step === STEPS.TEACHER && (
            <>
              {selectedBalance && (
                <div className="mb-4 px-3 py-2 bg-blue-50 rounded-lg text-sm">
                  <span className="text-gray-600">{selectedBalance.courseName}</span>
                  <span className="font-bold ml-2" style={{ color: colors.primary }}>
                    {(t.lessonsLeft || '%{count} lesson(s) left').replace('%{count}', selectedBalance.remainingLessons)}
                  </span>
                </div>
              )}
              {loadingTeachers ? (
                <div className="flex justify-center items-center py-12">
                  <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                </div>
              ) : teachers.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-2">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h4 className="font-medium text-gray-900">{t.noTeachersTitle || 'No teachers available to book'}</h4>
                  <p className="text-sm text-gray-500 mt-1">{t.noTeachersDescription || 'No teacher has been assigned to this course yet. Please try again later.'}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {teachers.map((teacher) => (
                    <TeacherCard
                      key={teacher.id}
                      teacher={teacher}
                      onSelect={() => handleTeacherSelect(teacher)}
                      showAvailabilityBadge={false}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Step: Time Slot Selection — Calendar + TimeSlotList */}
          {step === STEPS.TIMESLOT && (
            <>
              {selectedTeacher && (
                <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg">
                  <UserAvatar
                    src={selectedTeacher.avatar_url}
                    name={selectedTeacher.name}
                    size="sm"
                  />
                  <div>
                    <span className="text-base font-medium text-gray-900">{selectedTeacher.name}</span>
                    {selectedCourseData && (
                      <p className="text-sm text-gray-500">{selectedCourseData.name} · {selectedCourseData.durationMinutes} {t.min || 'min'}</p>
                    )}
                  </div>
                </div>
              )}
              <div className="mb-4">
                <TimezoneSelector
                  userTimezone={userTimezone}
                  onTimezoneChange={handleTimezoneChange}
                />
              </div>
              {loadingSlots ? (
                <div className="flex justify-center items-center py-12">
                  <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                </div>
              ) : !hasAnySlots ? (
                <div className="text-center py-12">
                  {balanceExhausted ? (
                    <>
                      <div className="text-orange-400 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <h4 className="font-medium text-gray-900">
                        {selectedCourseData?.isTrial ? (t.trialUsedTitle || 'Trial lesson already used') : (t.noLessonsTitle || 'No lessons remaining for this course')}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedCourseData?.isTrial ? (t.trialUsedDescription || 'Each student can only take one trial lesson') : (t.noLessonsDescription || 'Please purchase more lessons to continue booking')}
                      </p>
                      <div className="flex flex-col items-center gap-2 mt-4">
                        <button
                          onClick={() => onPurchaseMore?.(selectedCourseId)}
                          className="px-6 py-2 rounded-lg font-medium text-white cursor-pointer"
                          style={{ background: colors.primaryGradient }}
                        >
                          {t.purchaseMore || 'Purchase More Lessons'}
                        </button>
                        <button
                          onClick={handleClose}
                          className="text-sm text-gray-400 hover:text-gray-600 cursor-pointer"
                        >
                          {t.close || 'Close'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-gray-400 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h4 className="font-medium text-gray-900">{t.noSlotsTitle || 'This teacher has no available slots'}</h4>
                      <p className="text-sm text-gray-500 mt-1">{t.noSlotsDescription || 'Please select another teacher or try again later.'}</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Calendar
                    availabilityData={availabilityData}
                    selectedDate={selectedDate}
                    onDateSelect={handleDateSelect}
                    currentMonth={currentMonth}
                    onMonthChange={handleMonthChange}
                    translations={{ available_dates: t.calendarAvailable, selected: t.calendarSelected, today: t.calendarToday }}
                  />
                  <div ref={timeSlotsRef}>
                  <AvailabilityTimeSlotList
                    slots={selectedDateSlots}
                    selectedDate={selectedDate}
                    selectedSlot={selectedSlot?.start || null}
                    onSlotSelect={handleSlotSelect}
                    duration={selectedCourseData?.durationMinutes}
                    translations={{ morning: t.morning, afternoon: t.afternoon, evening: t.evening, timezone_hint: t.timezoneHint, select_date: t.selectDate, select_date_hint: t.selectDateHint }}
                  />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - Booking controls for time slot step */}
        {step === STEPS.TIMESLOT && hasAnySlots && (
          <div className="p-4 border-t border-gray-100">
            <RecurringBookingPreview
              courseId={selectedCourseId}
              teacherId={selectedTeacher?.id}
              selectedSlot={selectedSlot}
              remainingLessons={selectedBalance?.remainingLessons || 0}
              timezone={selectedTimezone}
              onSubmit={handleBook}
              submitting={booking}
              translations={t.recurring || {}}
            />
            {error && (
              <div className="mt-2 text-sm text-red-600">{error}</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
